import React, { Component, Fragment } from 'react';
import styleable from 'react-styleable';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import cloneDeep from 'lodash/cloneDeep';
import { DataTable, DatePicker } from '@3plearning/react-components';
import css from './all-classes.scss';
import allClassesActions, {
    ACTIVITY_MASTERY_ALL_CLASSES_RETRIEVED
} from './all-classes-actions';
import {
    ALL_CLASSES_TABLE_CONFIG,
    TOTAL_ACTIVITIES,
    CLASS_NAME,
    CLASS_ID,
    ACTIVITY_RESULTS,
    ALL_YEARS_TABLE_CONFIG
} from './all-classes-constants';
import { ALL_CLASSES_STRENGTH_WEAKNESSES_TABLE_CONFIG } from '../common/strength-and-weaknesses-constants';
import ActivityStatusBar from '../../../components/activity-status-bar/activity-status-bar';
import {
    activityMasteryPropType,
    courseInsightPropType
} from '../../../components/activity-status-bar/activity-mastery-prop-type';
import AllClassesStrengthWeakness from './all-classes-strength-weakness/all-classes-strength-weakness';
import activeFiltersActions from '../../../components/report-filters/redux/active-filters/active-filters-actions';
import {
    SELECTED_CLASS,
    SELECTED_DATE,
    SELECTED_YEAR_GROUP,
    SELECTED_TEACHER
} from '../../../components/report-filters/filter-constants';
import { activeFiltersPropType } from '../../../components/report-filters/filter-prop-types';
import RouteHelper from '../../../helpers/route-helper';
import activeFiltersSelectors from '../../../components/report-filters/redux/active-filters/active-filters-selectors';
import FilterHelper from '../../../helpers/filter-helper';

import ActivityMasteryLegends from '../common/activity-mastery-legends/activity-mastery-legends';
import { translateTableLabels } from '../../../helpers/locale-helpers';

/**
 * AllClasses component
 * Displays Activity Mastery table and strengths & weaknesses tabs for all classes
 */
export class AllClasses extends Component {
    static propTypes = {
        activityMasteryData: PropTypes.shape({
            allClassesActivityMastery: activityMasteryPropType,
            courseStrengths: PropTypes.arrayOf(courseInsightPropType),
            courseWeaknesses: PropTypes.arrayOf(courseInsightPropType)
        }),
        activeFilters: activeFiltersPropType,
        isAdmin: PropTypes.bool,
        updateActiveFilter: PropTypes.func,
        invalidRequest: PropTypes.string,
        getActivityMasteryData: PropTypes.func
    };

    constructor(props) {
        super(props);

        this.isLandingPage = true;
        this.isAllYearsRoute = false;
        this.state = {
            tableConfig: ALL_CLASSES_TABLE_CONFIG,
            strengthWeaknessTableConfig: ALL_CLASSES_STRENGTH_WEAKNESSES_TABLE_CONFIG,
            strengthData: [],
            weaknessData: []
        };
        this.setRouteType(props, true);
        this.i18n = window.i18n;
    }

    componentDidMount() {
        const { activeFilters } = this.props;
        if (FilterHelper.shouldLoadAllClasses(activeFilters)) {
            this.getAllClassesActivityMasteryData(this.props);
        }
    }

    componentWillReceiveProps(newProps) {
        const {
            activeFilters,
            activityMasteryData,
            invalidRequest,
            updateActiveFilter,
            isAdmin
        } = this.props;
        const {
            activeFilters: newActiveFilters,
            activityMasteryData: newActivityMasteryData,
            invalidRequest: newInvalidRequest,
            isAdmin: newIsAdmin
        } = newProps;

        // load activity mastery data if params change
        if (
            FilterHelper.shouldReloadAllClasses(activeFilters, newActiveFilters)
        ) {
            this.getAllClassesActivityMasteryData(newProps);
        }

        if (activeFilters !== newActiveFilters || isAdmin !== newIsAdmin) {
            this.setRouteType(newProps);
        }

        // set config when activity mastery data is returned from backend
        if (activityMasteryData !== newActivityMasteryData) {
            this.setTableConfig(newProps);

            this.setState({
                strengthData: newActivityMasteryData.courseStrengths,
                weaknessData: newActivityMasteryData.courseWeaknesses
            });
        }

        if (
            invalidRequest !== newInvalidRequest &&
            newInvalidRequest === ACTIVITY_MASTERY_ALL_CLASSES_RETRIEVED
        ) {
            if (
                newActiveFilters[SELECTED_DATE].type !==
                DatePicker.THIS_SCHOOL_YEAR
            ) {
                updateActiveFilter(SELECTED_DATE, {
                    type: DatePicker.THIS_SCHOOL_YEAR
                });
            }
        }
    }

    getAllClassesActivityMasteryData = (props) => {
        const { activeFilters, getActivityMasteryData } = props;

        this.setRouteType(props);
        getActivityMasteryData(
            activeFilters[SELECTED_DATE],
            activeFilters[SELECTED_YEAR_GROUP].id,
            activeFilters[SELECTED_TEACHER].id
        );
    };

    /**
     * Create table config by merging the data returned by the backend with
     * the TABLE_CONFIG constant
     * Some config is dependant on backend values, like the total activities column
     * header which displays the total number of all activities rather than a static string
     */
    setTableConfig = (props) => {
        const { activityMasteryData } = props;
        if (!activityMasteryData) {
            return;
        }
        const { allClassesActivityMastery } = activityMasteryData;

        let tableConfig;
        if (this.isAllYearsRoute) {
            tableConfig = cloneDeep(ALL_YEARS_TABLE_CONFIG);
        } else {
            tableConfig = cloneDeep(ALL_CLASSES_TABLE_CONFIG);
        }

        // hide back button on landing page
        if (this.isLandingPage) {
            tableConfig.colGroups[0][0].headerTransform = () => (
                <h3>{this.i18n._('Table Header - Activity Mastery')}</h3>
            );
        }

        const filterKey = this.isAllYearsRoute ? CLASS_ID : CLASS_NAME;

        tableConfig.cols.find(
            (col) => col.id === filterKey
        ).onClick = this.loadItemPage;
        tableConfig.cols.find(
            (col) => col.id === TOTAL_ACTIVITIES
        ).name = `${allClassesActivityMastery.totalActivities}`;
        tableConfig.cols.find(
            (col) => col.id === ACTIVITY_RESULTS
        ).headerTransform = () => (
            <ActivityStatusBar activityMastery={allClassesActivityMastery} />
        );

        tableConfig = translateTableLabels(tableConfig);

        this.setState({ tableConfig });
    };

    setStrengthWeaknessTableConfig() {
        const strengthWeaknessTableConfig = cloneDeep(
            ALL_CLASSES_STRENGTH_WEAKNESSES_TABLE_CONFIG
        );
        if (this.isAllYearsRoute) {
            strengthWeaknessTableConfig.cols[0].name = this.i18n._(
                'Export - All years'
            );
        }
        this.setState({ strengthWeaknessTableConfig });
    }

    setRouteType(props, firstLoad) {
        const { isAdmin, activeFilters } = props;
        this.isLandingPage = RouteHelper.isLandingPage(isAdmin, activeFilters);
        this.isAllYearsRoute = RouteHelper.isAllYearsRoute(
            isAdmin,
            activeFilters
        );
        if (!firstLoad) {
            this.setTableConfig(props);
            this.setStrengthWeaknessTableConfig();
        }
    }

    loadItemPage = (selectedItem) => {
        const itemType = this.isAllYearsRoute
            ? SELECTED_YEAR_GROUP
            : SELECTED_CLASS;
        this.props.updateActiveFilter(itemType, {
            id: selectedItem.id,
            name: selectedItem.name
        });
    };

    render() {
        const {
            tableConfig,
            strengthWeaknessTableConfig,
            strengthData,
            weaknessData
        } = this.state;
        const { activityMasteryData } = this.props;
        return (
            <Fragment>
                <ActivityMasteryLegends />
                <div className={css.component}>
                    <div className={css['data-table']}>
                        {activityMasteryData && (
                            <DataTable
                                config={tableConfig}
                                data={
                                    activityMasteryData.classroomActivityMasterys
                                }
                            />
                        )}
                    </div>
                    <div className={css['strength-weakness']}>
                        <AllClassesStrengthWeakness
                            tableConfig={strengthWeaknessTableConfig}
                            strengths={strengthData}
                            weaknesses={weaknessData}
                        />
                    </div>
                </div>
            </Fragment>
        );
    }
}

function mapStateToProps(state) {
    return {
        activityMasteryData: state.activityMasteryAllClasses,
        isAdmin: state.isAdmin,
        activeFilters: activeFiltersSelectors.selectActiveFilters(state),
        invalidRequest: state.invalidRequest
    };
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators(
        {
            getActivityMasteryData: allClassesActions.getActivityMasteryData,
            updateActiveFilter: activeFiltersActions.updateActiveFilter
        },
        dispatch
    );
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(styleable(css)(AllClasses));



// WEBPACK FOOTER //
// ./containers/activity-mastery/all-classes/all-classes.js